import { AcademicCapIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../../Avatar';
import { formatCreatedDate } from '../../../utils/dates';
import type { OrthodontistDetailInfo } from '../../../services/orthodontistAccounts';
import { PracticeMetaCell } from '../practice/PracticeMetaCell';
import { DETAIL_TABS, type DetailTabId } from './detailTabs';

function Breadcrumb({ name }: { name: string }) {
    const navigate = useNavigate();
    return (
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500 min-w-0">
            <button
                type="button"
                onClick={() => navigate('/accounts/orthodontists')}
                className="hover:text-gray-800 transition-colors"
            >
                Accounts
            </button>
            <ChevronRightIcon className="h-3 w-3 text-gray-300" />
            <button
                type="button"
                onClick={() => navigate('/accounts/orthodontists')}
                className="hover:text-gray-800 transition-colors"
            >
                Orthodontists
            </button>
            <ChevronRightIcon className="h-3 w-3 text-gray-300" />
            <span className="font-semibold text-gray-700 truncate">{name}</span>
        </div>
    );
}

// Mirrors PracticeDetailHeader's TopStrip - this page also hides SidebarLayout's
// default header row (see App.tsx's `hideHeader` on this route).
function TopStrip({ name }: { name: string }) {
    return (
        <div className="flex items-center justify-between px-8 pt-4 pb-2 shrink-0">
            <Breadcrumb name={name} />
            <div className="w-10 h-10">
                <Avatar />
            </div>
        </div>
    );
}

interface TabBarProps {
    activeTab: DetailTabId;
    onChange: (tab: DetailTabId) => void;
}

function TabBar({ activeTab, onChange }: TabBarProps) {
    return (
        <div className="px-6 flex gap-6 border-t border-gray-100">
            {DETAIL_TABS.map((tab) => {
                const isActive = tab.id === activeTab;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => onChange(tab.id as DetailTabId)}
                        className={`pt-3 pb-3 text-sm border-b-2 ${
                            isActive
                                ? 'font-semibold text-gray-900 border-gray-900'
                                : 'font-medium text-gray-500 border-transparent hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}

interface OrthodontistDetailHeaderProps {
    orthodontist: OrthodontistDetailInfo;
    practiceCount: number;
    activeTab: DetailTabId;
    onTabChange: (tab: DetailTabId) => void;
}

// View-only counterpart to PracticeDetailHeader: editing an org is its own
// self-service job (orthodontist/src/pages/Account.tsx), not an admin one.
export function OrthodontistDetailHeader({
    orthodontist,
    practiceCount,
    activeTab,
    onTabChange
}: OrthodontistDetailHeaderProps) {
    const doctorName = `Dr. ${orthodontist.drFirstName} ${orthodontist.drLastName}`.trim();
    const hasLocation = Boolean(orthodontist.city && orthodontist.state);

    return (
        <>
            <TopStrip name={orthodontist.name} />
            <div className="px-8 pb-2 mt-6 shrink-0">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)]">
                    <div className="px-6 pt-4 pb-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                            <AcademicCapIcon className="h-4 w-4" />
                        </div>
                        <h1
                            className="text-[20px] leading-tight font-bold text-gray-900 truncate min-w-0 flex-1"
                            title={orthodontist.name}
                        >
                            {orthodontist.name}
                        </h1>
                    </div>
                    <div className="px-6 pb-4 grid grid-cols-6 gap-4">
                        <PracticeMetaCell label="Doctor">{doctorName}</PracticeMetaCell>
                        <PracticeMetaCell label="Location">
                            {hasLocation ? `${orthodontist.city}, ${orthodontist.state}` : null}
                        </PracticeMetaCell>
                        <PracticeMetaCell label="Phone">{orthodontist.phone}</PracticeMetaCell>
                        <PracticeMetaCell label="Email">{orthodontist.email}</PracticeMetaCell>
                        <PracticeMetaCell label="Practices">{practiceCount}</PracticeMetaCell>
                        <PracticeMetaCell label="Added">
                            {formatCreatedDate(orthodontist.createdAt)}
                        </PracticeMetaCell>
                    </div>
                    <TabBar activeTab={activeTab} onChange={onTabChange} />
                </div>
            </div>
        </>
    );
}
